import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import UniversalStatusBadge from '@/components/shared/UniversalStatusBadge';
import { Lock } from 'lucide-react';
import { CustomerCredit } from '@/hooks/useCustomerCredits';
import { useCreditEligibleInvoices } from '@/hooks/useCreditEligibleInvoices';
import { formatCurrency } from '@/lib/utils';

interface ApplyCreditsToInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  credit: CustomerCredit | null;
  customerId: string;
  onApply: (data: { creditId: string; invoiceId: string; amount: number }) => void;
  isLoading: boolean;
}

const ApplyCreditsToInvoiceModal: React.FC<ApplyCreditsToInvoiceModalProps> = ({
  isOpen,
  onClose,
  credit,
  customerId,
  onApply,
  isLoading,
}) => {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');
  const [applyAmount, setApplyAmount] = useState<number | ''>(0);

  // Use the new hook for project-scoped credit eligibility
  const { 
    eligibleInvoices, 
    isLoading: loadingInvoices, 
    isCreditLocked,
    lockedToProjectName 
  } = useCreditEligibleInvoices(
    isOpen ? credit?.id || null : null, 
    isOpen ? customerId : null
  );

  // Calculate balance due for each invoice (accounting for credits already applied)
  const getBalanceDue = (invoice: { total_amount: number | null; paid_amount: number | null; credit_applied?: number | null }) => {
    const netAmount = (invoice.total_amount || 0) - (invoice.credit_applied || 0);
    return netAmount - (invoice.paid_amount || 0);
  };

  // Set max apply amount when invoice is selected
  useEffect(() => {
    if (selectedInvoiceId && credit && eligibleInvoices) {
      const selectedInvoice = eligibleInvoices.find((inv) => inv.id === selectedInvoiceId);
      if (selectedInvoice) {
        const balanceDue = getBalanceDue(selectedInvoice);
        const maxApply = Math.min(credit.remaining_amount, balanceDue);
        setApplyAmount(maxApply);
      }
    }
  }, [selectedInvoiceId, credit, eligibleInvoices]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedInvoiceId('');
      setApplyAmount(0);
    }
  }, [isOpen]);

  const handleApply = () => {
    const effectiveAmount = applyAmount === '' ? 0 : applyAmount;
    if (!credit || !selectedInvoiceId || effectiveAmount <= 0) return;
    onApply({
      creditId: credit.id,
      invoiceId: selectedInvoiceId,
      amount: effectiveAmount,
    });
    onClose();
  };


  const renderStatusBadge = (status: string) => (
    <UniversalStatusBadge entityType="invoice" status={status} size="sm" showTooltip={false} />
  );

  const selectedInvoice = eligibleInvoices?.find((inv) => inv.id === selectedInvoiceId);
  const maxApplyAmount = selectedInvoice
    ? Math.min(credit?.remaining_amount || 0, getBalanceDue(selectedInvoice))
    : credit?.remaining_amount || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply Credit to Invoice</DialogTitle>
        </DialogHeader>

        {credit && (
          <div className="bg-muted rounded-md p-4 mb-4">
            <p className="text-sm text-muted-foreground">
              Available credit:{' '}
              <span className="font-semibold text-foreground">{formatCurrency(credit.remaining_amount)}</span>
            </p>
          </div>
        )}

        {/* Show project lock info */}
        {isCreditLocked && lockedToProjectName && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 flex items-start gap-2">
            <Lock className="h-4 w-4 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Credit locked to project</p>
              <p className="text-yellow-700">
                This credit can only be applied to invoices from "{lockedToProjectName}" until the project is completed, on hold, or cancelled.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Select Invoice</Label>
            {loadingInvoices ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : eligibleInvoices && eligibleInvoices.length > 0 ? (
              <RadioGroup value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {eligibleInvoices.map((invoice) => {
                    const balanceDue = getBalanceDue(invoice);
                    return (
                      <label
                        key={invoice.id}
                        className="flex items-center justify-between p-3 border rounded-md cursor-pointer hover:bg-accent"
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value={invoice.id} id={invoice.id} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">
                                {invoice.invoice_number || 'No number'}
                              </p>
                              {renderStatusBadge(invoice.status)}
                            </div>
                            <p className="text-xs text-muted-foreground">{invoice.title}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm">{formatCurrency(balanceDue)}</p>
                          <p className="text-xs text-muted-foreground">Balance due</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </RadioGroup>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No eligible invoices found for this customer</p>
                {isCreditLocked && (
                  <p className="text-xs mt-2">
                    This credit is locked to a specific project. Create invoices for that project first.
                  </p>
                )}
              </div>
            )}
          </div>

          {selectedInvoiceId && (
            <div>
              <Label htmlFor="applyAmount">Amount to Apply</Label>
              <Input
                id="applyAmount"
                type="number"
                step="0.01"
                min="0.01"
                max={maxApplyAmount}
                value={applyAmount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setApplyAmount('');
                  else { const num = parseFloat(val); if (!isNaN(num)) setApplyAmount(num); }
                }}
                onBlur={() => { if (applyAmount === '') setApplyAmount(0); }}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum: {formatCurrency(maxApplyAmount)}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={isLoading || !selectedInvoiceId || (applyAmount === '' ? 0 : applyAmount) <= 0 || (applyAmount === '' ? 0 : applyAmount) > maxApplyAmount}
          >
            {isLoading ? 'Applying...' : `Apply ${formatCurrency(applyAmount === '' ? 0 : applyAmount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyCreditsToInvoiceModal;
